import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Edit2,
  Trash2,
  TrendingDown,
  X,
  Eye,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Rows3,
} from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import { api, fmtINR, formatApiError } from "../lib/api";
import MoneyInput from "../components/ui/MoneyInput";
import DateField from "../components/ui/DateField";
import { toast } from "sonner";
import "../styles/date.css";

const LIAB_CATEGORIES = [
  { value: "home_loan", label: "Home Loan" },
  { value: "car_loan", label: "Car Loan" },
  { value: "personal_loan", label: "Personal Loan" },
  { value: "credit_card", label: "Credit Card" },
  { value: "education_loan", label: "Education Loan" },
  { value: "mortgage", label: "Mortgage" },
  { value: "business_debt", label: "Business Debt" },
  { value: "informal", label: "Informal" },
  { value: "other", label: "Other" },
];

const CATEGORY_LABEL = {
  ...Object.fromEntries(LIAB_CATEGORIES.map((c) => [c.value, c.label])),
  insurance_premium: "Insurance Premium",
};

// Per-type behaviour for the add/edit form.
//   emi        → default the EMI block on
//   tenure     → default the tenure block on
//   revolving  → no EMI/tenure (e.g. credit card), but interest still shown
//   rateLabel  → label for the interest field
//   originalLabel / balanceLabel → tailored amount labels
//   defaultRate → pre-filled interest rate
//   note       → helper text shown under the form
const LIAB_TYPE_CONFIG = {
  home_loan: { emi: true, tenure: true, rateLabel: "Interest Rate % p.a.", originalLabel: "Loan Amount (₹)", defaultRate: 8.5 },
  car_loan: { emi: true, tenure: true, rateLabel: "Interest Rate % p.a.", originalLabel: "Loan Amount (₹)", defaultRate: 9.5 },
  education_loan: { emi: true, tenure: true, rateLabel: "Interest Rate % p.a.", originalLabel: "Loan Amount (₹)", defaultRate: 9, note: "Add EMI start after any moratorium/study period." },
  mortgage: { emi: true, tenure: true, rateLabel: "Interest Rate % p.a.", originalLabel: "Loan Amount (₹)", defaultRate: 9 },
  personal_loan: { emi: true, tenure: true, rateLabel: "Interest Rate % p.a.", originalLabel: "Loan Amount (₹)", defaultRate: 14 },
  business_debt: { emi: true, tenure: true, rateLabel: "Interest Rate % p.a.", originalLabel: "Sanctioned Amount (₹)", defaultRate: 12 },
  credit_card: {
    revolving: true,
    rateLabel: "APR / Interest % p.a.",
    originalLabel: "Credit Limit (₹)",
    balanceLabel: "Current Balance (₹)",
    defaultRate: 42,
    note: "Revolving credit — no fixed EMI. Cards typically charge 36–42% p.a. on carried balances.",
  },
  informal: { rateLabel: "Interest % p.a. (if any)", originalLabel: "Borrowed Amount (₹)", optionalRate: true, note: "Loans from family/friends — interest optional." },
  other: { rateLabel: "Interest % p.a." },
};

const liabCfg = (cat) => LIAB_TYPE_CONFIG[cat] || LIAB_TYPE_CONFIG.other;

const isSynthetic = (l) => l?.synthetic === true || l?.source === "insurance";

const todayISO = () => new Date().toISOString().slice(0, 10);

const PAGE_SIZE_OPTIONS = [10, 25, 50];

function Pagination({ total, page, pageSize, onPageChange, onPageSizeChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (safePage > 3) pages.push("…");
      for (
        let i = Math.max(2, safePage - 1);
        i <= Math.min(totalPages - 1, safePage + 1);
        i++
      )
        pages.push(i);
      if (safePage < totalPages - 2) pages.push("…");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-slate-100 dark:border-slate-800">
      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span>
          Showing{" "}
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {start + 1}–{Math.min(start + pageSize, total)}
          </span>{" "}
          of{" "}
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {total}
          </span>
        </span>
        <span className="hidden sm:inline text-slate-300 dark:text-slate-700">
          |
        </span>
        <div className="hidden sm:flex items-center gap-1.5">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(Math.max(1, safePage - 1))}
          disabled={safePage === 1}
          className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {getPageNumbers().map((p, i) =>
          p === "…" ? (
            <span
              key={`e-${i}`}
              className="px-1 text-xs text-slate-400 select-none"
            >
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`min-w-[32px] h-8 rounded-lg text-xs font-medium transition ${
                safePage === p
                  ? "bg-emerald-500 text-white shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              {p}
            </button>
          ),
        )}
        <button
          onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
          disabled={safePage === totalPages}
          className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function Liabilities() {
  const { viewOnly } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  function getOrdinal(n) {
    if (n > 3 && n < 21) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/liabilities");
      setList(data.liabilities || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handlePageSizeChange = (s) => {
    setPageSize(s);
    setPage(1);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this liability?")) return;
    try {
      await api.delete(`/liabilities/${id}`);
      toast.success("Liability deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Closed / paid-off liabilities (is_active === false) are still listed but
  // must not count toward outstanding debt or monthly EMI.
  const activeList = list.filter((a) => a.is_active !== false);
  const total = activeList.reduce((s, a) => s + (Number(a.outstanding) || 0), 0);
  const totalEMI = activeList.reduce((s, a) => s + (Number(a.emi) || 0), 0);

  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedList = list.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">
            Your debt
          </p>
          <h1
            data-testid="liab-title"
            className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
          >
            Liabilities
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Outstanding:{" "}
            <span className="num-mono font-bold text-rose-600">
              {fmtINR(total)}
            </span>{" "}
            · Monthly EMIs:{" "}
            <span className="num-mono font-bold">{fmtINR(totalEMI)}</span>
          </p>
        </div>
        {!viewOnly && (
          <div className="flex items-center gap-2">
            <Link
              data-testid="open-bulk-liability"
              to="/dashboard/bulk-add/liabilities"
              className="btn-secondary"
            >
              <Rows3 className="h-4 w-4" /> Bulk add
            </Link>
            <button
              data-testid="open-add-liability"
              onClick={() => {
                setEditing(null);
                setShowModal(true);
              }}
              className="btn-primary"
            >
              <Plus className="h-4 w-4" /> Add liability
            </button>
          </div>
        )}
      </div>

      <div className="card-soft overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-slate-500">
            Loading…
          </div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center">
            <TrendingDown className="h-10 w-10 text-slate-300 mx-auto" />
            <h3 className="mt-3 font-display font-bold">No liabilities</h3>
            <p className="mt-1 text-sm text-slate-500">
              Nothing to worry about — or add one to track.
            </p>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 dark:border-slate-800">
                  <th className="text-left px-6 py-4 font-semibold">Name</th>
                  <th className="text-left px-6 py-4 font-semibold hidden md:table-cell">
                    Category
                  </th>
                  <th className="text-right px-6 py-4 font-semibold">
                    Outstanding
                  </th>
                  <th className="text-right px-6 py-4 font-semibold hidden md:table-cell">
                    EMI
                  </th>
                  <th className="text-right px-6 py-4 font-semibold hidden lg:table-cell">
                    Rate
                  </th>
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {pagedList.map((l) => (
                  <tr
                    key={l.id}
                    data-testid={`liab-row-${l.id}`}
                    className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                  >
                    <td className="px-6 py-5">
                      <div className="flex items-start gap-3">
                        <div className="mt-1 grid h-10 w-10 place-items-center rounded-2xl bg-rose-50 dark:bg-rose-500/10">
                          <TrendingDown className="h-5 w-5 text-rose-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 dark:text-white">
                            {l.name}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            {l.lender || "No lender"}
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="rounded-full bg-rose-50 dark:bg-rose-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-rose-700 dark:text-rose-300">
                              {CATEGORY_LABEL[l.category] || l.category}
                            </span>
                            {isSynthetic(l) && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 dark:bg-sky-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-sky-700 dark:text-sky-300">
                                <ShieldCheck className="h-3 w-3" /> Insurance
                              </span>
                            )}
                            {l.is_active ? (
                              <span className="rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
                                Active
                              </span>
                            ) : (
                              <span className="rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-700 dark:text-slate-300">
                                Closed
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-right">
                      <div className="text-lg font-bold num-mono text-rose-600">
                        {fmtINR(l.outstanding)}
                      </div>
                      {l.original_amount && (
                        <div className="mt-1 text-[11px] text-slate-500">
                          Original: {fmtINR(l.original_amount)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-5 hidden md:table-cell text-right">
                      <div className="text-sm font-bold num-mono text-slate-900 dark:text-white">
                        {l.emi ? fmtINR(l.emi) : "—"}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        {l.emi_date
                          ? `Due on ${l.emi_date}${getOrdinal(l.emi_date)}`
                          : "No EMI date"}
                      </div>
                    </td>
                    <td className="px-6 py-5 hidden lg:table-cell text-right">
                      <div className="text-sm font-semibold text-slate-900 dark:text-white">
                        {l.tenure_years ? `${l.tenure_years} Years` : "—"}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        {l.end_date || "No end date"}
                      </div>
                    </td>
                    <td className="px-6 py-5 hidden xl:table-cell text-right">
                      <div className="text-sm font-bold text-slate-900 dark:text-white">
                        {l.interest_rate ? `${l.interest_rate}%` : "—"}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        Interest
                      </div>
                    </td>
                    <td className="px-6 py-5 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setViewing(l)}
                          className="rounded-xl p-2 text-slate-500 transition-colors hover:bg-sky-50 hover:text-sky-600 dark:hover:bg-sky-500/10"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {isSynthetic(l) ? (
                          <Link
                            to="/dashboard/insurance"
                            title="Managed in the Insurance tab"
                            className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-sky-600 dark:hover:bg-slate-800"
                          >
                            <ShieldCheck className="h-4 w-4" />
                          </Link>
                        ) : viewOnly ? null : (
                          <>
                            <button
                              data-testid={`edit-liab-${l.id}`}
                              onClick={() => {
                                setEditing(l);
                                setShowModal(true);
                              }}
                              className="rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-emerald-600 dark:hover:bg-slate-800"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              data-testid={`delete-liab-${l.id}`}
                              onClick={() => onDelete(l.id)}
                              className="rounded-xl p-2 text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              total={list.length}
              page={safePage}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
        )}
      </div>

      {showModal && (
        <LiabilityModal
          liab={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            load();
          }}
        />
      )}
      {viewing && (
        <LiabilityViewModal
          liability={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </DashboardLayout>
  );
}

function LiabilityModal({ liab, onClose, onSaved }) {
  const initCfg = liabCfg(liab?.category || "home_loan");
  const [form, setForm] = useState({
    name: liab?.name || "",
    category: liab?.category || "home_loan",
    outstanding: liab?.outstanding || "",
    original_amount: liab?.original_amount || "",
    interest_rate: liab?.interest_rate ?? "",
    emi: liab?.emi || "",
    emi_date: liab?.emi_date || "",
    lender: liab?.lender || "",
    notes: liab?.notes || "",
    is_emi: liab?.is_emi ?? !!initCfg.emi,
    has_tenure: liab?.has_tenure ?? !!initCfg.tenure,
    start_date: liab?.start_date ? String(liab.start_date).slice(0, 10) : "",
    end_date: liab?.end_date ? String(liab.end_date).slice(0, 10) : "",
    tenure_years: liab?.tenure_years || "",
    is_active: liab?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const cfg = liabCfg(form.category);
  const showRate = !cfg.revolving ? true : true; // interest relevant for all here
  const showEmi = form.is_emi && !cfg.revolving;
  const showTenure = (form.is_emi || form.has_tenure) && !cfg.revolving;

  // Snap EMI/tenure/rate defaults to the chosen type.
  const onCategoryChange = (category) => {
    const c = liabCfg(category);
    setForm((f) => ({
      ...f,
      category,
      is_emi: c.revolving ? false : !!c.emi,
      has_tenure: c.revolving ? false : !!c.tenure,
      interest_rate:
        f.interest_rate === "" && c.defaultRate != null
          ? String(c.defaultRate)
          : f.interest_rate,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const numOr = (v) => (v !== "" && v != null ? Number(String(v).replace(/,/g, "")) : undefined);
      const payload = {
        name: form.name,
        category: form.category,
        outstanding: Number(String(form.outstanding).replace(/,/g, "")),
        original_amount: numOr(form.original_amount),
        lender: form.lender || undefined,
        notes: form.notes || undefined,
        is_active: !!form.is_active,
        interest_rate: numOr(form.interest_rate),
        is_emi: showEmi,
        emi: showEmi ? numOr(form.emi) : undefined,
        emi_date: showEmi && form.emi_date !== "" ? Number(form.emi_date) : undefined,
        has_tenure: showTenure,
      };
      if (showTenure) {
        // Tenure = (end − start) in whole years; start defaults to today.
        const start = form.start_date || todayISO();
        let tenure = numOr(form.tenure_years);
        if (!tenure && form.end_date) {
          const yrs =
            (new Date(form.end_date) - new Date(start)) /
            (365.25 * 24 * 3600 * 1000);
          if (yrs > 0) tenure = Math.max(1, Math.round(yrs));
        }
        payload.start_date = start;
        payload.end_date = form.end_date || undefined;
        payload.tenure_years = tenure;
      }
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      if (liab?.id) await api.put(`/liabilities/${liab.id}`, payload);
      else await api.post("/liabilities", payload);
      toast.success(liab ? "Liability updated" : "Liability added");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="w-full max-w-lg card-soft p-6 my-8 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">
              {liab ? "Edit liability" : "Add liability"}
            </h2>
            <button
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <form onSubmit={onSubmit} className="space-y-3">
            <Field label="Name" required>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-soft"
                placeholder="e.g. HDFC Home Loan"
              />
            </Field>
            <Field label="Category" required>
              <select
                value={form.category}
                onChange={(e) => onCategoryChange(e.target.value)}
                className="input-soft"
              >
                {LIAB_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={cfg.originalLabel || "Original (₹)"}>
                <MoneyInput
                  value={form.original_amount}
                  onValueChange={(raw) =>
                    setForm({ ...form, original_amount: raw })
                  }
                  placeholder="0"
                />
              </Field>
              <Field label={cfg.balanceLabel || "Outstanding (₹)"} required>
                <MoneyInput
                  required
                  value={form.outstanding}
                  onValueChange={(raw) => setForm({ ...form, outstanding: raw })}
                  placeholder="0"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={cfg.lenderLabel || "Lender"}>
                <input
                  value={form.lender}
                  onChange={(e) => setForm({ ...form, lender: e.target.value })}
                  className="input-soft"
                  placeholder="e.g. HDFC Bank"
                />
              </Field>
              {showRate && (
                <Field label={cfg.rateLabel || "Interest Rate %"}>
                  <input
                    type="number"
                    step="0.1"
                    value={form.interest_rate}
                    onChange={(e) =>
                      setForm({ ...form, interest_rate: e.target.value })
                    }
                    className="input-soft"
                    placeholder={cfg.defaultRate != null ? String(cfg.defaultRate) : "0"}
                  />
                </Field>
              )}
            </div>

            {/* EMI block — hidden for revolving credit */}
            {!cfg.revolving && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-slate-900 dark:text-white">
                      Has EMI?
                    </div>
                    <div className="text-xs text-slate-500">
                      Recurring monthly payment
                    </div>
                  </div>
                  <Toggle on={form.is_emi} onClick={() => setForm({ ...form, is_emi: !form.is_emi })} />
                </div>
                {form.is_emi && (
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <Field label="Monthly EMI (₹)">
                      <MoneyInput
                        value={form.emi}
                        onValueChange={(raw) => setForm({ ...form, emi: raw })}
                        placeholder="0"
                      />
                    </Field>
                    <Field label="EMI Day (1–31)">
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={form.emi_date}
                        onChange={(e) => setForm({ ...form, emi_date: e.target.value })}
                        className="input-soft"
                        placeholder="5"
                      />
                    </Field>
                  </div>
                )}
              </div>
            )}

            {/* Tenure block */}
            {!cfg.revolving && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-slate-900 dark:text-white">
                      Has Tenure?
                    </div>
                    <div className="text-xs text-slate-500">
                      Fixed repayment duration
                    </div>
                  </div>
                  <Toggle
                    on={form.has_tenure}
                    onClick={() => setForm({ ...form, has_tenure: !form.has_tenure })}
                  />
                </div>
                {showTenure && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    <Field label="Start Date">
                      <DateField
                        value={form.start_date}
                        onChange={(v) => setForm({ ...form, start_date: v })}
                      />
                    </Field>
                    <Field label="End Date">
                      <DateField
                        value={form.end_date}
                        onChange={(v) => setForm({ ...form, end_date: v })}
                      />
                    </Field>
                    <Field label="Tenure (Years)">
                      <input
                        type="number"
                        min="0"
                        value={form.tenure_years}
                        onChange={(e) => setForm({ ...form, tenure_years: e.target.value })}
                        className="input-soft"
                        placeholder="20"
                      />
                    </Field>
                    <p className="sm:col-span-3 text-[11px] text-slate-400">
                      Leave tenure blank to compute it from start → end date. If
                      no start date is given, <b>today's date</b> is used.
                    </p>
                  </div>
                )}
              </div>
            )}

            <Field label="Status">
              <select
                value={form.is_active ? "active" : "closed"}
                onChange={(e) => setForm({ ...form, is_active: e.target.value === "active" })}
                className="input-soft"
              >
                <option value="active">Active</option>
                <option value="closed">Closed / Paid off</option>
              </select>
            </Field>

            {cfg.note && (
              <p className="text-[11px] text-slate-400 leading-relaxed">{cfg.note}</p>
            )}

            <Field label="Notes">
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="input-soft min-h-[60px]"
                rows={2}
              />
            </Field>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="btn-primary flex-1">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Toggle({ on, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative h-6 w-11 rounded-full transition ${on ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"}`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${on ? "left-5" : "left-0.5"}`}
      />
    </button>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </label>
      {children}
    </div>
  );
}

function LiabilityViewModal({ liability, onClose }) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="card-soft my-8 max-h-[90vh] w-full max-w-2xl overflow-y-auto p-6">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
                {liability.name}
              </h2>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-red-100 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-red-700 dark:bg-red-500/20 dark:text-red-300">
                  {CATEGORY_LABEL[liability.category] || liability.category}
                </span>
                {liability.is_active ? (
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                    Active
                  </span>
                ) : (
                  <span className="rounded-full bg-slate-200 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                    Closed
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="grid h-9 w-9 place-items-center rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-3xl border border-red-100 bg-red-50 p-5 dark:border-red-500/20 dark:bg-red-500/10">
              <div className="text-[11px] font-bold uppercase tracking-widest text-red-600 dark:text-red-300">
                Outstanding
              </div>
              <div className="mt-2 text-3xl font-bold text-red-700 dark:text-red-200 num-mono">
                {fmtINR(liability.outstanding)}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-800/50">
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                EMI
              </div>
              <div className="mt-2 text-3xl font-bold text-slate-900 dark:text-white num-mono">
                {liability.emi ? fmtINR(liability.emi) : "—"}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-800/50">
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                Interest Rate
              </div>
              <div className="mt-2 text-3xl font-bold text-slate-900 dark:text-white num-mono">
                {liability.interest_rate ? `${liability.interest_rate}%` : "—"}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ViewField
              label="Original Amount"
              value={
                liability.original_amount
                  ? fmtINR(liability.original_amount)
                  : "—"
              }
            />
            <ViewField
              label="Outstanding Amount"
              value={fmtINR(liability.outstanding)}
            />
            <ViewField label="Lender" value={liability.lender || "—"} />
            <ViewField
              label="Category"
              value={CATEGORY_LABEL[liability.category] || liability.category}
            />
            <ViewField
              label="EMI Enabled"
              value={liability.is_emi ? "Yes" : "No"}
            />
            <ViewField
              label="EMI Date"
              value={
                liability.emi_date
                  ? `${liability.emi_date} of every month`
                  : "—"
              }
            />
            <ViewField
              label="Interest Rate"
              value={
                liability.interest_rate ? `${liability.interest_rate}%` : "—"
              }
            />
            <ViewField
              label="Active Status"
              value={liability.is_active ? "Active" : "Closed"}
            />
            <ViewField
              label="Has Tenure"
              value={liability.has_tenure ? "Yes" : "No"}
            />
            <ViewField
              label="Tenure"
              value={
                liability.tenure_years ? `${liability.tenure_years} Years` : "—"
              }
            />
            <ViewField label="Start Date" value={liability.start_date || "—"} />
            <ViewField label="End Date" value={liability.end_date || "—"} />
            <ViewField
              label="Created At"
              value={
                liability.created_at?.slice(0, 10)?.replaceAll("-", "/") || "—"
              }
            />
            <ViewField
              label="Updated At"
              value={
                liability.updated_at?.slice(0, 10)?.replaceAll("-", "/") || "—"
              }
            />
          </div>
          <div className="mt-6">
            <ViewField label="Notes" value={liability.notes || "—"} full />
          </div>
        </div>
      </div>
    </div>
  );
}

function ViewField({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
        {label}
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3 text-sm text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-900/40">
        {value}
      </div>
    </div>
  );
}
